// src/app/services/tenant-config.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export enum TenantsEnum{
  ASBAR = 'asbar',
  MULKIA = 'mulkia',
  VALUE_CAPITAL = 'valueCapital',
  DEFAULT = 'default',
}
export enum MenuStyle {
  SIDE_MENU = 'side-menu',
  TOP_MENU = 'top-menu',
}
export enum FooterStyle {
  SIMPLE = 'simple',
  DETAILED = 'detailed',
}
export enum AppTheme {
  MODERN = 'modern',
  CLASSIC = 'classic',
}
export enum DialogsTheme {
  ASIDE = 'aside',
  BOTTOM_SHEET = 'bottom_sheet',
  MODAL = 'modal',
}
export interface TenantConfig {
   coloredLogoUrl:string;
   whiteLogoUrl:string;
   sidePageImageUrl:string;
   defaultOpportunityImageUrl:string;
  homeMobileAppSplashImageUrl?:string;
   primaryColor:string;
   secondaryColor:string;
   mediumPrimaryColor:string;
   lightPrimaryColor:string;
   successColor:string;
   errorColor:string;
   warningColor:string;
   infoColor:string;
  tenantId: string;
  tenantName: string;
  apisBaseUrl: string;
  notificationServiceBaseUrl: string;
  awsUrl: string;
  mapKey: string;
  moyassarPublishableKey:string;
  themeCssPath?: string; // optional explicit path; defaults to assets/themes/{tenantId}.css
  menuStyle: MenuStyle;
  showWelcomeMessageInAuthScreens:boolean;
  showInvestTypesBeforeRegistration:boolean;
  opportunityTheme: AppTheme;
  opportunityDetailsTheme: AppTheme;
  profileSettingsTheme: AppTheme;
  footerStyle: FooterStyle;
  dialogsTheme: DialogsTheme;
  hasLandingPage:boolean;
  googlePlayDownloadUrl:string;
  appleStoreDownloadUrl:string;
  twitterUrl:string;
  linkedinUrl:string;
  supportEmail:string;
  tollFreeNumber?:string;
  isOnlinePaymentEnabled:boolean;
  isArabBankTransferEnabled:boolean;
}

@Injectable({ providedIn: 'root' })
export class TenantConfigService {
  private config!: TenantConfig;

  constructor() {}

  loadConfig(){
    const host = window.location.hostname;
    console.log(host);
    const tenantMap = environment.tenantsMapper;

    const configKey = tenantMap[host as keyof typeof tenantMap] || environment.tenantsMapper['default'];

    const raw = environment.tenantConfigs[configKey as keyof typeof environment.tenantConfigs] as any;
    // Coerce string values from environment into strongly-typed enums
    this.config = {
      ...raw,
      menuStyle: raw.menuStyle as MenuStyle,
      footerStyle: raw.footerStyle as FooterStyle,
      opportunityTheme: raw.opportunityTheme as AppTheme,
      opportunityDetailsTheme: raw.opportunityDetailsTheme as AppTheme,
      dialogsTheme: raw.dialogsTheme as DialogsTheme,
    } as TenantConfig;
    this.applyTheme(this.config);
  }

  get tenant(): TenantConfig {
    return this.config;
  }

  private applyTheme(cfg: TenantConfig) {
    const root = document.documentElement;
    if (!root) {
      return;
    }

    if (cfg && cfg.primaryColor) {
      root.style.setProperty('--primary-color', cfg.primaryColor as string);
    }

    if (cfg && cfg.secondaryColor) {
      root.style.setProperty('--secondary-color', cfg.secondaryColor as string);
    }

    if (cfg && cfg.mediumPrimaryColor) {
      root.style.setProperty('--medium-primary-color', cfg.mediumPrimaryColor as string);
    }

    if (cfg && cfg.lightPrimaryColor) {
      root.style.setProperty('--light-primary-color', cfg.lightPrimaryColor as string);
    }

    if (cfg && cfg.successColor) {
      root.style.setProperty('--success-color', cfg.successColor as string);
    }

    if (cfg && cfg.errorColor) {
      root.style.setProperty('--error-color', cfg.errorColor as string);
    }

    if (cfg && cfg.warningColor) {
      root.style.setProperty('--warning-color', cfg.warningColor as string);
    }

    if (cfg && cfg.infoColor) {
      root.style.setProperty('--info-color', cfg.infoColor as string);
    }

    if (cfg && cfg.tenantName) {
      document.title = cfg.tenantName;
    }
    // Provide CSS vars for tenant assets used in global styles
    root.style.setProperty('--tenant-side-image', `url('${cfg.sidePageImageUrl}')`);
    let tenantLogo = cfg.coloredLogoUrl;
    if (cfg && tenantLogo) {
      const existing = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
      const link: HTMLLinkElement = existing || document.createElement('link');
      link.rel = 'icon';
      link.href = tenantLogo;
      if (tenantLogo.endsWith('.svg')) {
        link.type = 'image/svg+xml';
      } else if (tenantLogo.endsWith('.png')) {
        link.type = 'image/png';
      } else if (tenantLogo.endsWith('.ico')) {
        link.type = 'image/x-icon';
      }
      if (!existing) {
        document.head.appendChild(link);
      }
    }

    // Inject or swap tenant stylesheet
    const themeLinkId = 'tenant-theme-styles';
    const themeHref = (cfg.themeCssPath && cfg.themeCssPath.trim()) || `assets/themes/${cfg.tenantId}.css`;
    let themeLink = document.getElementById(themeLinkId) as HTMLLinkElement | null;
    if (!themeLink) {
      themeLink = document.createElement('link');
      themeLink.id = themeLinkId;
      themeLink.rel = 'stylesheet';
      document.head.appendChild(themeLink);
    }
    // Only update if different (account for absolute href resolution)
    const currentHref = themeLink.getAttribute('href');
    if (currentHref !== themeHref) {
      themeLink.setAttribute('href', themeHref);
    }
  }
}
